import { FC, useMemo } from "react";
import { VerificationTemplate } from "../";
import { useVerificationTemplateController } from "views/verification-templates-id/controller";
import { Loader } from "shared/components/Loader";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { XClose } from "untitledui-js-base";
import {
  Template,
  VerificationSchemas
} from "types/landlord/verificationTemplate";
import { ApplicantType, VerificationNames } from "shared/types";
import { LayoutHeader } from "../components/LayoutHeader";
import { SideBar } from "../components/SideBar";
import { LayoutFooter } from "../components/LayoutFooter";

type Props = {
  opened: boolean;
  onClose: () => void;
  id?: string;
  template?: Template;
};

export const TemplateReviewModal: FC<Props> = ({
  opened,
  onClose,
  id,
  template: propTemplate
}) => {
  const { verificationSchemas, templateQuery, defaultTemplate } =
    useVerificationTemplateController(id);

  const template = useMemo(() => {
    if (propTemplate) return propTemplate;
    if (templateQuery.data?.verification_template) {
      const currentVerificationTemplate =
        templateQuery.data.verification_template;

      if (defaultTemplate)
        currentVerificationTemplate.verification_schemas = [
          "applicant",
          "co_signer"
        ].reduce(
          (acc, prev) => {
            const initialSchemas = defaultTemplate.verification_schemas[prev];

            const currentSchemas = currentVerificationTemplate
              .verification_schemas[prev]
              ? currentVerificationTemplate.verification_schemas[prev]
              : initialSchemas;

            const requiredFieldSteps = [
              "pets",
              "emergency_contacts",
              "vehicles"
            ];
            const rentalApplicationRequired = requiredFieldSteps.reduce(
              (acc, step) => {
                const key = `${step}_step_required`;
                return {
                  ...acc,
                  [key]: {
                    ...initialSchemas.rental_application[key],
                    ...currentSchemas.rental_application[key]
                  }
                };
              },
              {}
            );

            currentSchemas.rental_application = {
              ...currentSchemas.rental_application,
              ...rentalApplicationRequired
            };

            currentSchemas.income_verification = {
              ...initialSchemas.income_verification,
              ...currentSchemas.income_verification
            };

            return {
              ...acc,
              [prev]: currentSchemas
            };
          },
          { applicant: {}, co_signer: {} }
        ) as VerificationSchemas;

      currentVerificationTemplate.settings = {
        ...currentVerificationTemplate.settings,
        applicant: {
          ...currentVerificationTemplate.settings.applicant,
          enabled_screening_services: true
        },
        co_signer: {
          ...currentVerificationTemplate.settings.co_signer,
          enabled_screening_services:
            currentVerificationTemplate.settings.co_signer
              .enabled_screening_services ?? true
        }
      };

      return currentVerificationTemplate;
    }
  }, [
    propTemplate,
    defaultTemplate,
    templateQuery.data?.verification_template
  ]);

  if (!defaultTemplate) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="mx-auto h-[90dvh] w-[90dvw] overflow-y-auto overscroll-none bg-background-900 rounded-lg overflow-hidden"
    >
      {templateQuery.isLoading ? (
        <Loader />
      ) : !template ? (
        <>
          <div className="flex justify-end p-6">
            <IconButton
              Icon={XClose}
              onClick={onClose}
              size={IconButtonSize.SM}
              borderLess
            />
          </div>
          <div className="flex flex-1 items-center justify-center">
            <h1 className="text-lg font-bold">Template not found</h1>
          </div>
        </>
      ) : (
        <VerificationTemplate
          layoutProps={{
            displayAside: false,
            header: (
              <>
                <div className="flex p-4 border-b border-alpha-40 justify-end bg-background-1000">
                  <IconButton
                    variant={IconButtonVariant.BASE}
                    size={IconButtonSize.SM}
                    borderLess
                    onClick={onClose}
                    Icon={XClose}
                  />
                </div>
                <LayoutHeader />
              </>
            ),
            sidebar: (
              <SideBar isModal template={template} editedName={template.name} />
            ),
            footer: <LayoutFooter onCancel={onClose} />
          }}
          verificationTemplate={template}
          savedTemplate={defaultTemplate}
          verificationSchemas={verificationSchemas || []}
          readOnly
          isModal
          originalQualificationsDisclosureText={
            template.verification_schemas[ApplicantType.Applicant][
              VerificationNames.TermsAndConditions
            ]?.additional_text || ""
          }
          originalSubmissionDisclosureText={
            template.verification_schemas[ApplicantType.Applicant][
              VerificationNames.SubmissionDisclosure
            ]?.additional_text || ""
          }
        />
      )}
    </Modal>
  );
};
